import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI, authAPI, mfaAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { ConfirmModal } from '../components/modals';
import AddUserModal from '../components/AddUserModal';
import EditUserModal from '../components/EditUserModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import AddHouseholdModal from '../components/AddHouseholdModal';
import CustomSelect from '../components/common/CustomSelect';
import SearchInput from '../components/common/SearchInput';
import TextInput from '../components/common/TextInput';
import Toggle from '../components/common/Toggle';
import FilterDropdown from '../components/common/FilterDropdown';
import LoadingButton from '../components/common/LoadingButton';
import { Spinner } from '../components/common/LoadingComponents';
import Tooltip from '../components/common/Tooltip';
import { useIsMobile } from '../hooks';
import { 
  Shield, 
  Users, 
  Lock, 
  Database,
  UserPlus,
  Mail,
  Home,
  CheckCircle,
  XCircle,
  Trash2,
  Send,
  Server,
  Loader2,
  Eye,
  EyeOff,
  AlertTriangle,
  KeyRound,
  ShieldOff,
  Globe,
  Link,
  Key,
  Wrench,
  Clock,
  Info,
  ChevronDown,
  ChevronRight,
  Image,
  ExternalLink,
  Plug
} from 'lucide-react';
import '../styles/Admin.css';

function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Valid tab IDs - New structure:
  // Users, Households, Security, Single Sign-On, Email, Application, Integrations, Maintenance, Database, About
  const validTabs = ['users', 'households', 'security', 'sso', 'email', 'application', 'integrations', 'maintenance', 'database', 'about'];
  
  // Initialize tab from URL or default to 'users'
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    // Handle old tab names for backwards compatibility
    if (tabFromUrl === 'auth') return 'security';
    if (tabFromUrl === 'system') return 'application';
    return validTabs.includes(tabFromUrl) ? tabFromUrl : 'users';
  });

  const isMobile = useIsMobile(1024); // Use mobile layout for tablet sizes too
  // On mobile, start with menu open only if no tab is specified in URL
  const [mobileMenuOpen, setMobileMenuOpen] = useState(() => {
    const urlTab = searchParams.get('tab');
    return !urlTab; // Show menu only if no tab in URL
  });

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'households', label: 'Households', icon: Home },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'sso', label: 'Single Sign-On', icon: Key },
    { id: 'email', label: 'Email / SMTP', icon: Mail },
    { id: 'application', label: 'Application', icon: Globe },
    { id: 'integrations', label: 'Integrations', icon: Plug },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'about', label: 'About', icon: Info },
  ];

  // Sync URL when tab changes
  // Only sync URL when tab changes, not on initial load
  const [hasUserChangedTab, setHasUserChangedTab] = useState(false);
  
  useEffect(() => {
    if (hasUserChangedTab) {
      const currentTab = searchParams.get('tab');
      if (currentTab !== activeTab) {
        setSearchParams({ tab: activeTab }, { replace: true });
      }
    }
  }, [activeTab, searchParams, setSearchParams, hasUserChangedTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setHasUserChangedTab(true);
    if (isMobile) {
      setMobileMenuOpen(false); // Hide menu, show content
    }
    // Scroll to top of content
    const contentArea = document.querySelector('.sidebar-layout__content');
    if (contentArea) {
      contentArea.scrollTop = 0;
    }
  };

  const handleMobileBack = () => {
    setMobileMenuOpen(true); // Show menu again
    // Clear the tab from URL so refresh stays on menu
    setSearchParams({}, { replace: true });
  };

  // Render the tab content (shared between mobile and desktop)
  const renderContent = () => (
    <>
      {activeTab === 'users' && <ApplicationUsers />}
      {activeTab === 'households' && <HouseholdsManagement />}
      {activeTab === 'security' && <SecuritySettings />}
      {activeTab === 'sso' && <SSOSettings />}
      {activeTab === 'email' && <EmailSettings />}
      {activeTab === 'application' && <ApplicationSettings />}
      {activeTab === 'integrations' && <IntegrationsSettings />}
      {activeTab === 'maintenance' && <MaintenanceSettings />}
      {activeTab === 'database' && <DatabaseManagement />}
      {activeTab === 'about' && <AboutSettings />}
    </>
  );

  return (
    <div className="page admin-page">
      <div className={`sidebar-layout ${isMobile ? 'sidebar-layout--mobile-menu' : ''}`}>
        {isMobile ? (
          /* Mobile: Full Page List Pattern */
          mobileMenuOpen ? (
            /* Menu List */
            <div className="sidebar-layout__mobile-menu">
              <div className="sidebar-layout__mobile-header">
                <h2>Admin Settings</h2>
              </div>
              <div className="sidebar-layout__mobile-list">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      className={`sidebar-layout__mobile-item ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <Icon size={20} />
                      <span>{tab.label}</span>
                      <ChevronRight size={18} className="chevron" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Content View */
            <div className="sidebar-layout__mobile-content">
              <button className="sidebar-layout__mobile-back" onClick={handleMobileBack}>
                <ChevronRight size={16} className="back-icon" />
                <span>Admin Settings</span>
              </button>
              <div className="sidebar-layout__content admin-content">
                {renderContent()}
              </div>
            </div>
          )
        ) : (
          /* Desktop Layout */
          <>
            <div className="sidebar-layout__nav">
              <div className="sidebar-layout__nav-inner">
                <nav className="nav-tabs nav-tabs--vertical nav-tabs--admin">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                      >
                        <Icon size={20} />
                        <span className="nav-tab__label">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            <div className="sidebar-layout__content admin-content">
              {renderContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Application Users - Admin only
function ApplicationUsers() {
  const isMobile = useIsMobile();
  const { success, error: showError, info: showInfo } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [addUserModal, setAddUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState({ isOpen: false, user: null });
  const [promoteModal, setPromoteModal] = useState({ isOpen: false, user: null });
  const [toggleActiveModal, setToggleActiveModal] = useState({ isOpen: false, user: null });
  const [resetPasswordModal, setResetPasswordModal] = useState({ isOpen: false, user: null, newPassword: '' });
  const [resetMFAModal, setResetMFAModal] = useState({ isOpen: false, user: null });

  const [resendingInvite, setResendingInvite] = useState(null);

  // Mock data - will be replaced with API call
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      const response = await adminAPI.createUser(userData);
      if (response.data.inviteSent) {
        success('User created and invitation email sent');
      } else {
        success('User created successfully');
      }
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error; // Re-throw to be handled by modal
    }
  };

  const handleEditUser = async (userId, userData) => {
    try {
      await adminAPI.updateUser(userId, userData);
      success('User updated successfully');
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error; // Re-throw to be handled by modal
    }
  };

  const handlePromoteToAdmin = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setPromoteModal({ 
      isOpen: true, 
      user: user 
    });
  };

  const confirmPromote = async () => {
    if (!promoteModal.user) return;

    try {
      await adminAPI.promoteToAdmin(promoteModal.user.id);
      success('User promoted to admin successfully');
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to promote user:', error);
      showError(error.response?.data?.error || 'Failed to promote user to admin');
    }
  };

  const handleToggleActive = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setToggleActiveModal({ 
      isOpen: true, 
      user: user 
    });
  };

  const confirmToggleActive = async () => {
    if (!toggleActiveModal.user) return;

    const user = toggleActiveModal.user;
    try {
      await adminAPI.toggleUserStatus(user.id, !user.is_active);
      success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      showError(error.response?.data?.error || 'Failed to update user status');
    }
  };

  const handleResetPassword = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    try {
      // Load password requirements from settings
      const response = await adminAPI.getSettings('authentication');
      const settings = response.data.setting_value;
      
      // Generate a password that meets requirements
      const newPassword = generatePasswordWithRequirements(settings);

      setResetPasswordModal({ 
        isOpen: true, 
        user: user,
        newPassword: newPassword
      });
    } catch (error) {
      console.error('Failed to load password requirements:', error);
      // Fallback to basic generation
      const newPassword = generateRandomPassword();
      setResetPasswordModal({ 
        isOpen: true, 
        user: user,
        newPassword: newPassword
      });
    }
  };

  const generatePasswordWithRequirements = (settings) => {
    const length = Math.max(settings.passwordMinLength || 8, 12);
    let charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    if (settings.passwordRequireSpecialChars) {
      charset += '!@#$%^&*';
    }
    
    let password = '';
    
    // Ensure at least one of each required character type
    if (settings.passwordRequireUppercase) {
      password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(Math.floor(Math.random() * 26));
    }
    if (settings.passwordRequireLowercase) {
      password += 'abcdefghijklmnopqrstuvwxyz'.charAt(Math.floor(Math.random() * 26));
    }
    if (settings.passwordRequireNumbers) {
      password += '0123456789'.charAt(Math.floor(Math.random() * 10));
    }
    if (settings.passwordRequireSpecialChars) {
      password += '!@#$%^&*'.charAt(Math.floor(Math.random() * 8));
    }
    
    // Fill the rest randomly
    while (password.length < length) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const generateRandomPassword = () => {
    // Fallback generator for backwards compatibility
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const confirmResetPassword = async (newPassword) => {
    if (!resetPasswordModal.user) return;

    try {
      await adminAPI.resetPassword(resetPasswordModal.user.id, newPassword);
      success(`Password reset successfully for ${resetPasswordModal.user.first_name} ${resetPasswordModal.user.last_name}`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      showError(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleResetMFA = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Only show if user has MFA enabled
    if (!user.mfa_enabled) {
      showInfo('This user does not have MFA enabled');
      return;
    }

    setResetMFAModal({ 
      isOpen: true, 
      user: user 
    });
  };

  const confirmResetMFA = async () => {
    if (!resetMFAModal.user) return;

    try {
      await mfaAPI.adminReset(resetMFAModal.user.id);
      success(`MFA reset successfully for ${resetMFAModal.user.first_name} ${resetMFAModal.user.last_name}`);
      loadUsers(); // Reload to update MFA status
    } catch (error) {
      console.error('Failed to reset MFA:', error);
      showError(error.response?.data?.error || 'Failed to reset MFA');
    }
  };

  const handleResendInvite = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setResendingInvite(userId);
    try {
      await adminAPI.resendInvite(userId);
      success(`Invitation email resent to ${user.email}`);
    } catch (error) {
      console.error('Failed to resend invite:', error);
      showError(error.response?.data?.error || 'Failed to resend invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setDeleteModal({ 
      isOpen: true, 
      user: user 
    });
  };

  const confirmDelete = async () => {
    if (!deleteModal.user) return;

    try {
      await adminAPI.deleteUser(deleteModal.user.id);
      success('User deleted successfully');
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to delete user:', error);
      showError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.household_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Filter config for FilterDropdown
  const filterConfig = [
    {
      id: 'role',
      label: 'Role',
      type: 'select',
      value: roleFilter,
      onChange: setRoleFilter,
      options: [
        { value: 'all', label: 'All Roles' },
        { value: 'admin', label: 'Admins' },
        { value: 'member', label: 'Members' }
      ]
    },
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    }
  ];

  const activeFilterCount = [
    roleFilter !== 'all',
    statusFilter !== 'all'
  ].filter(Boolean).length;

  // Helper to generate invited-by tooltip content
  const getInvitedByTooltip = (user) => {
    if (!user.invited_by) return null;
    const invitedBy = user.invited_by;
    const invitedDate = invitedBy.invitedAt ? new Date(invitedBy.invitedAt).toLocaleDateString() : null;
    return (
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 500, marginBottom: '4px' }}>Invited by {invitedBy.name}</div>
        {invitedBy.householdName && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>to {invitedBy.householdName}</div>}
        {invitedDate && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{invitedDate}</div>}
      </div>
    );
  };

  // Render status badge with optional tooltip
  const renderStatusBadge = (user) => {
    const tooltipContent = user.has_pending_invite ? getInvitedByTooltip(user) : null;
    const badge = (
      <span className={`status-badge ${user.is_active ? 'active' : user.has_pending_invite ? 'invited' : 'inactive'}`}>
        {user.is_active ? <CheckCircle size={12} /> : user.has_pending_invite ? <Mail size={12} /> : <XCircle size={12} />}
        {user.is_active ? 'Active' : user.has_pending_invite ? 'Invited' : 'Inactive'}
      </span>
    );
    
    if (tooltipContent) {
      return <Tooltip content={tooltipContent} position="top">{badge}</Tooltip>;
    }
    return badge;
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <div>
          <h2>Application Users</h2>
          <p className="section-description">Manage all users across all households</p>
        </div>
        <button className="btn-primary" onClick={() => setAddUserModal(true)}>
          <UserPlus size={18} />
          <span>Add User</span>
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search users..."
        />

        <FilterDropdown
          filters={filterConfig}
          activeCount={activeFilterCount}
        />
      </div>

      {/* Users Table/Cards */}
      {loading ? (
        <div className="loading-state">Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>No users found</h3>
          <p>Try adjusting your search or filters</p>
        </div>
      ) : isMobile ? (
        /* Mobile Card View */
        <div className="mobile-card-list">
          {filteredUsers.map(user => (
            <div key={user.id} className="mobile-card">
              <div 
                className="mobile-card-header"
                onClick={() => setEditUserModal({ isOpen: true, user })}
                style={{ cursor: 'pointer' }}
              >
                <div className="user-avatar">
                  {user.first_name[0]}{user.last_name[0]}
                </div>
                <div className="user-details">
                  <div className="user-name">{user.first_name} {user.last_name}</div>
                  <div className="user-email">
                    <Mail size={12} />
                    {user.email}
                  </div>
                </div>
              </div>
              <div className="mobile-card-body">
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Role</span>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'admin' && <Shield size={12} />}
                    {user.role}
                  </span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Status</span>
                  {renderStatusBadge(user)}
                </div>
              </div>
              <div className="mobile-card-footer">
                <div className="action-buttons">
                  {user.role !== 'admin' && (
                    <button
                      className="btn-icon"
                      onClick={() => handlePromoteToAdmin(user.id)}
                      title="Promote to Admin"
                    >
                      <Shield size={16} />
                    </button>
                  )}
                  {!user.is_active && user.has_pending_invite && (
                    <button
                      className="btn-icon"
                      onClick={() => handleResendInvite(user.id)}
                      title="Resend Invitation"
                      disabled={resendingInvite === user.id}
                    >
                      {resendingInvite === user.id ? <Spinner size="sm" /> : <Send size={16} />}
                    </button>
                  )}
                  <button
                    className="btn-icon"
                    onClick={() => handleToggleActive(user.id)}
                    title={user.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {user.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                  </button>
                  {!user.sso_only && (
                    <button
                      className="btn-icon"
                      onClick={() => handleResetPassword(user.id)}
                      title="Reset Password"
                    >
                      <KeyRound size={16} />
                    </button>
                  )}
                  {user.mfa_enabled && (
                    <button
                      className="btn-icon"
                      onClick={() => handleResetMFA(user.id)}
                      title="Reset MFA"
                    >
                      <ShieldOff size={16} />
                    </button>
                  )}
                  <button
                    className="btn-icon danger"
                    onClick={() => handleDeleteUser(user.id)}
                    title="Delete User"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>
                    <div 
                      className="user-cell clickable" 
                      onClick={() => setEditUserModal({ isOpen: true, user })}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="user-avatar">
                        {user.first_name[0]}{user.last_name[0]}
                      </div>
                      <div className="user-details">
                        <div className="user-name">{user.first_name} {user.last_name}</div>
                        <div className="user-email">
                          <Mail size={12} />
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role === 'admin' && <Shield size={12} />}
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {renderStatusBadge(user)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {user.role !== 'admin' && (
                        <button
                          className="btn-icon"
                          onClick={() => handlePromoteToAdmin(user.id)}
                          title="Promote to Admin"
                        >
                          <Shield size={16} />
                        </button>
                      )}
                      {!user.is_active && user.has_pending_invite && (
                        <button
                          className="btn-icon"
                          onClick={() => handleResendInvite(user.id)}
                          title="Resend Invitation"
                          disabled={resendingInvite === user.id}
                        >
                          {resendingInvite === user.id ? <Spinner size="sm" /> : <Send size={16} />}
                        </button>
                      )}
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleActive(user.id)}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                      </button>
                      {!user.sso_only && (
                        <button
                          className="btn-icon"
                          onClick={() => handleResetPassword(user.id)}
                          title="Reset Password"
                        >
                          <KeyRound size={16} />
                        </button>
                      )}
                      {user.mfa_enabled && (
                        <button
                          className="btn-icon"
                          onClick={() => handleResetMFA(user.id)}
                          title="Reset MFA"
                        >
                          <ShieldOff size={16} />
                        </button>
                      )}
                      <button
                        className="btn-icon danger"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-footer">
        <p>Showing {filteredUsers.length} of {users.length} users</p>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteModal.user?.first_name} ${deleteModal.user?.last_name}? This action cannot be undone.`}
        confirmText="Delete User"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Add User Modal */}
      <AddUserModal
        isOpen={addUserModal}
        onClose={() => setAddUserModal(false)}
        onSubmit={handleAddUser}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={editUserModal.isOpen}
        onClose={() => setEditUserModal({ isOpen: false, user: null })}
        onSubmit={handleEditUser}
        user={editUserModal.user}
      />

      {/* Promote to Admin Confirmation Modal */}
      <ConfirmModal
        isOpen={promoteModal.isOpen}
        onClose={() => setPromoteModal({ isOpen: false, user: null })}
        onConfirm={confirmPromote}
        title="Promote to Administrator"
        message={`Are you sure you want to promote ${promoteModal.user?.first_name} ${promoteModal.user?.last_name} to Administrator? They will have full application access.`}
        confirmText="Promote to Admin"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Toggle Active Status Confirmation Modal */}
      <ConfirmModal
        isOpen={toggleActiveModal.isOpen}
        onClose={() => setToggleActiveModal({ isOpen: false, user: null })}
        onConfirm={confirmToggleActive}
        title={toggleActiveModal.user?.is_active ? 'Deactivate User' : 'Activate User'}
        message={
          toggleActiveModal.user?.is_active 
            ? `Are you sure you want to deactivate ${toggleActiveModal.user?.first_name} ${toggleActiveModal.user?.last_name}? They will not be able to log in.`
            : `Are you sure you want to activate ${toggleActiveModal.user?.first_name} ${toggleActiveModal.user?.last_name}? They will be able to log in again.`
        }
        confirmText={toggleActiveModal.user?.is_active ? 'Deactivate User' : 'Activate User'}
        cancelText="Cancel"
        variant={toggleActiveModal.user?.is_active ? 'warning' : 'info'}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={resetPasswordModal.isOpen}
        onClose={() => setResetPasswordModal({ isOpen: false, user: null, newPassword: '' })}
        onConfirm={confirmResetPassword}
        user={resetPasswordModal.user}
        newPassword={resetPasswordModal.newPassword}
      />

      {/* Reset MFA Confirmation Modal */}
      <ConfirmModal
        isOpen={resetMFAModal.isOpen}
        onClose={() => setResetMFAModal({ isOpen: false, user: null })}
        onConfirm={confirmResetMFA}
        title="Reset Two-Factor Authentication"
        message={`Are you sure you want to reset MFA for ${resetMFAModal.user?.first_name} ${resetMFAModal.user?.last_name}? They will need to set up 2FA again on their next login.`}
        confirmText="Reset MFA"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
}

// Households Management - Admin only
function HouseholdsManagement() {
  const isMobile = useIsMobile();
  const { success, error: showError } = useToast();
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedHousehold, setExpandedHousehold] = useState(null);
  const [householdMembers, setHouseholdMembers] = useState({});
  const [deleteHouseholdModal, setDeleteHouseholdModal] = useState({ isOpen: false, household: null });
  const [addHouseholdModal, setAddHouseholdModal] = useState(false);
  const [promoteModal, setPromoteModal] = useState({ isOpen: false, user: null });
  const [demoteModal, setDemoteModal] = useState({ isOpen: false, user: null });
  const [removeUserModal, setRemoveUserModal] = useState({ isOpen: false, user: null });
  const [addMemberToHouseholdModal, setAddMemberToHouseholdModal] = useState({ isOpen: false, householdId: null, householdName: '' });

  useEffect(() => {
    loadHouseholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHouseholds = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllHouseholds();
      setHouseholds(response.data);
    } catch (error) {
      console.error('Failed to load households:', error);
      showError('Failed to load households');
    } finally {
      setLoading(false);
    }
  };

  const loadHouseholdMembers = async (householdId) => {
    try {
      const response = await adminAPI.getHouseholdMembers(householdId);
      setHouseholdMembers(prev => ({
        ...prev,
        [householdId]: response.data
      }));
    } catch (error) {
      console.error('Failed to load household members:', error);
      showError('Failed to load household members');
    }
  };

  const toggleHousehold = async (householdId) => {
    if (expandedHousehold === householdId) {
      setExpandedHousehold(null);
    } else {
      setExpandedHousehold(householdId);
      if (!householdMembers[householdId]) {
        await loadHouseholdMembers(householdId);
      }
    }
  };

  const handleDeleteHousehold = (household) => {
    setDeleteHouseholdModal({ isOpen: true, household });
  };

  const confirmDeleteHousehold = async () => {
    if (!deleteHouseholdModal.household) return;
    try {
      await adminAPI.deleteHousehold(deleteHouseholdModal.household.id);
      success(`Household "${deleteHouseholdModal.household.name}" deleted successfully`);
      setDeleteHouseholdModal({ isOpen: false, household: null });
      if (expandedHousehold === deleteHouseholdModal.household.id) {
        setExpandedHousehold(null);
      }
      loadHouseholds();
    } catch (error) {
      console.error('Failed to delete household:', error);
      showError(error.response?.data?.error || 'Failed to delete household');
    }
  };

  const confirmAddHousehold = async (name) => {
    try {
      await adminAPI.createHousehold({ name });
      success(`Household "${name}" created successfully`);
      setAddHouseholdModal(false);
      loadHouseholds();
    } catch (error) {
      console.error('Failed to create household:', error);
      throw error;
    }
  };

  const handlePromoteToManager = (user) => {
    setPromoteModal({ isOpen: true, user });
  };

  const handleDemoteToMember = (user) => {
    setDemoteModal({ isOpen: true, user });
  };

  const handleRemoveFromHousehold = (user) => {
    setRemoveUserModal({ isOpen: true, user });
  };

  const confirmPromoteToManager = async () => {
    if (!promoteModal.user) return;
    try {
      await adminAPI.updateUser(promoteModal.user.id, { householdRole: 'manager' });
      success(`${promoteModal.user.first_name} ${promoteModal.user.last_name} promoted to household manager`);
      setPromoteModal({ isOpen: false, user: null });
      if (expandedHousehold) {
        await loadHouseholdMembers(expandedHousehold);
      }
    } catch (error) {
      console.error('Failed to promote user:', error);
      showError('Failed to promote user to manager');
    }
  };

  const confirmDemoteToMember = async () => {
    if (!demoteModal.user) return;
    try {
      await adminAPI.updateUser(demoteModal.user.id, { householdRole: 'member' });
      success(`${demoteModal.user.first_name} ${demoteModal.user.last_name} demoted to household member`);
      setDemoteModal({ isOpen: false, user: null });
      if (expandedHousehold) {
        await loadHouseholdMembers(expandedHousehold);
      }
    } catch (error) {
      console.error('Failed to demote user:', error);
      showError(error.response?.data?.error || 'Failed to demote user to member');
    }
  };

  const confirmRemoveFromHousehold = async () => {
    if (!removeUserModal.user) return;
    try {
      const currentHouseholdMembers = householdMembers[expandedHousehold] || [];
      const managerCount = currentHouseholdMembers.filter(m => m.household_role === 'manager').length;
      
      if (removeUserModal.user.household_role === 'manager' && managerCount <= 1) {
        showError('Cannot remove the last manager from the household. Promote another member to manager first.');
        setRemoveUserModal({ isOpen: false, user: null });
        return;
      }

      await adminAPI.updateUser(removeUserModal.user.id, { householdId: null });
      success(`${removeUserModal.user.first_name} ${removeUserModal.user.last_name} removed from household`);
      setRemoveUserModal({ isOpen: false, user: null });
      if (expandedHousehold) {
        await loadHouseholdMembers(expandedHousehold);
      }
      loadHouseholds();
    } catch (error) {
      console.error('Failed to remove user from household:', error);
      setRemoveUserModal({ isOpen: false, user: null });
      showError(error.response?.data?.error || 'Failed to remove user from household');
    }
  };

  const confirmAddMemberToHousehold = async (userId, role) => {
    try {
      await adminAPI.updateUser(userId, { 
        householdId: addMemberToHouseholdModal.householdId, 
        householdRole: role 
      });
      success('Member added to household successfully');
      setAddMemberToHouseholdModal({ isOpen: false, householdId: null, householdName: '' });
      if (expandedHousehold) {
        await loadHouseholdMembers(expandedHousehold);
      }
      loadHouseholds();
    } catch (error) {
      console.error('Failed to add member to household:', error);
      throw error;
    }
  };

  const filteredHouseholds = households.filter(household => {
    return household.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="admin-section">
      <div className="section-header">
        <div>
          <h2>Households</h2>
          <p className="section-description">View all households and their members</p>
        </div>
        <button className="btn-primary" onClick={() => setAddHouseholdModal(true)}>
          <Home size={18} />
          <span>Add Household</span>
        </button>
      </div>

      {/* Search */}
      <div className="filters-bar">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search households..."
        />
      </div>

      {/* Households Table/Cards */}
      {loading ? (
        <div className="loading-state">Loading households...</div>
      ) : filteredHouseholds.length === 0 ? (
        <div className="empty-state">
          <Home size={48} />
          <h3>No households found</h3>
          <p>Try adjusting your search</p>
        </div>
      ) : isMobile ? (
        /* Mobile Card View */
        <div className="mobile-card-list">
          {filteredHouseholds.map(household => (
            <div key={household.id} className="mobile-card">
              <div 
                className="mobile-card-header"
                onClick={() => toggleHousehold(household.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="user-avatar">
                  <Home size={16} />
                </div>
                <div className="user-details">
                  <div className="user-name">{household.name}</div>
                </div>
                <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
                  {expandedHousehold === household.id ? '▼' : '▶'}
                </span>
              </div>
              <div className="mobile-card-body">
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Members</span>
                  <span>{household.member_count} {household.member_count === 1 ? 'member' : 'members'}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Created</span>
                  <span>{new Date(household.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              {/* Expanded Members Section */}
              {expandedHousehold === household.id && (
                <div className="mobile-card-expanded">
                  <div className="mobile-card-expanded-header">
                    <strong>Members ({householdMembers[household.id]?.length || 0})</strong>
                  </div>
                  {householdMembers[household.id] ? (
                    householdMembers[household.id].length === 0 ? (
                      <div className="no-members" style={{ padding: 'var(--spacing-md)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <p>No members in this household</p>
                      </div>
                    ) : (
                      <div className="mobile-member-list">
                        {householdMembers[household.id].map(member => (
                          <div key={member.id} className="mobile-member-item">
                            <div className="mobile-member-info">
                              <div className="user-avatar" style={{ width: '28px', height: '28px', fontSize: '0.65rem' }}>
                                {member.first_name[0]}{member.last_name[0]}
                              </div>
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{member.first_name} {member.last_name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                  {member.household_role === 'manager' ? '👑 Manager' : '👤 Member'}
                                </div>
                              </div>
                            </div>
                            <div className="action-buttons">
                              {member.household_role !== 'manager' ? (
                                <button
                                  className="btn-icon"
                                  onClick={() => handlePromoteToManager(member)}
                                  title="Promote to Manager"
                                  style={{ fontSize: '0.9rem' }}
                                >
                                  👑
                                </button>
                              ) : (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleDemoteToMember(member)}
                                  title="Demote to Member"
                                  style={{ fontSize: '0.9rem' }}
                                >
                                  👤
                                </button>
                              )}
                              <button
                                className="btn-icon danger"
                                onClick={() => handleRemoveFromHousehold(member)}
                                title="Remove from Household"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <div style={{ padding: 'var(--spacing-md)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      Loading members...
                    </div>
                  )}
                </div>
              )}
              <div className="mobile-card-footer">
                <div className="action-buttons">
                  <button
                    className="btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddMemberToHouseholdModal({
                        isOpen: true,
                        householdId: household.id,
                        householdName: household.name
                      });
                    }}
                    title="Add Member"
                  >
                    <UserPlus size={16} />
                  </button>
                  <button
                    className="btn-icon danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteHousehold(household);
                    }}
                    title="Delete Household"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Household</th>
                <th>Members</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHouseholds.map(household => (
                <React.Fragment key={household.id}>
                  <tr 
                    onClick={() => toggleHousehold(household.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          <Home size={16} />
                        </div>
                        <div className="user-details">
                          <div className="user-name">{household.name}</div>
                        </div>
                      </div>
                    </td>
                    <td>{household.member_count} {household.member_count === 1 ? 'member' : 'members'}</td>
                    <td>{new Date(household.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddMemberToHouseholdModal({
                              isOpen: true,
                              householdId: household.id,
                              householdName: household.name
                            });
                          }}
                          title="Add Member"
                        >
                          <UserPlus size={16} />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteHousehold(household);
                          }}
                          title="Delete Household"
                        >
                          <Trash2 size={16} />
                        </button>
                        <span style={{ color: 'var(--text-secondary)', padding: '0 4px' }}>
                          {expandedHousehold === household.id ? '▼' : '▶'}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Members Row */}
                  {expandedHousehold === household.id && (
                    <tr>
                      <td colSpan="4" style={{ padding: 0, backgroundColor: 'var(--background-color)' }}>
                        <div style={{ padding: '16px' }}>
                          <div className="households-members-header">
                            <h4>Members ({householdMembers[household.id]?.length || 0})</h4>
                          </div>
                          {householdMembers[household.id] ? (
                            householdMembers[household.id].length === 0 ? (
                              <div className="no-members">
                                <p>No members in this household</p>
                              </div>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Member</th>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Role</th>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Status</th>
                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {householdMembers[household.id].map(member => (
                                    <tr key={member.id}>
                                      <td style={{ padding: '8px 12px' }}>
                                        <div className="user-cell">
                                          <div className="user-avatar">
                                            {member.first_name[0]}{member.last_name[0]}
                                          </div>
                                          <div className="user-details">
                                            <div className="user-name">{member.first_name} {member.last_name}</div>
                                            <div className="user-email">{member.email}</div>
                                          </div>
                                        </div>
                                      </td>
                                      <td style={{ padding: '8px 12px' }}>
                                        <span className={`role-badge ${member.household_role}`}>
                                          {member.household_role === 'manager' ? '👑 Manager' : '👤 Member'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '8px 12px' }}>
                                        <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                                          {member.is_active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                          {member.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '8px 12px' }}>
                                        <div className="action-buttons">
                                          {member.household_role !== 'manager' ? (
                                            <button
                                              className="btn-icon"
                                              onClick={() => handlePromoteToManager(member)}
                                              title="Promote to Manager"
                                            >
                                              👑
                                            </button>
                                          ) : (
                                            <button
                                              className="btn-icon"
                                              onClick={() => handleDemoteToMember(member)}
                                              title="Demote to Member"
                                            >
                                              👤
                                            </button>
                                          )}
                                          <button
                                            className="btn-icon danger"
                                            onClick={() => handleRemoveFromHousehold(member)}
                                            title="Remove from Household"
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )
                          ) : (
                            <div className="loading-state">Loading members...</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-footer">
        <p>Showing {filteredHouseholds.length} of {households.length} households</p>
      </div>

      {/* Delete Household Modal */}
      <ConfirmModal
        isOpen={deleteHouseholdModal.isOpen}
        onClose={() => setDeleteHouseholdModal({ isOpen: false, household: null })}
        onConfirm={confirmDeleteHousehold}
        title="Delete Household"
        message={`Are you sure you want to delete "${deleteHouseholdModal.household?.name}"? This household has ${deleteHouseholdModal.household?.member_count || 0} member(s) who will be unassigned. This action cannot be undone.`}
        confirmText="Delete Household"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Add Household Modal */}
      <AddHouseholdModal
        isOpen={addHouseholdModal}
        onClose={() => setAddHouseholdModal(false)}
        onSubmit={confirmAddHousehold}
      />

      {/* Promote to Manager Modal */}
      <ConfirmModal
        isOpen={promoteModal.isOpen}
        onClose={() => setPromoteModal({ isOpen: false, user: null })}
        onConfirm={confirmPromoteToManager}
        title="Promote to Household Manager"
        message={`Are you sure you want to promote ${promoteModal.user?.first_name} ${promoteModal.user?.last_name} to household manager?`}
        confirmText="Promote to Manager"
        cancelText="Cancel"
        variant="info"
      />

      {/* Demote to Member Modal */}
      <ConfirmModal
        isOpen={demoteModal.isOpen}
        onClose={() => setDemoteModal({ isOpen: false, user: null })}
        onConfirm={confirmDemoteToMember}
        title="Demote to Household Member"
        message={`Are you sure you want to demote ${demoteModal.user?.first_name} ${demoteModal.user?.last_name} to household member?`}
        confirmText="Demote to Member"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Remove from Household Modal */}
      <ConfirmModal
        isOpen={removeUserModal.isOpen}
        onClose={() => setRemoveUserModal({ isOpen: false, user: null })}
        onConfirm={confirmRemoveFromHousehold}
        title="Remove from Household"
        message={`Are you sure you want to remove ${removeUserModal.user?.first_name} ${removeUserModal.user?.last_name} from this household? They will remain in the system but will not be part of any household.`}
        confirmText="Remove from Household"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Add Member to Household Modal */}
      <AddMemberToHouseholdModal
        isOpen={addMemberToHouseholdModal.isOpen}
        onClose={() => setAddMemberToHouseholdModal({ isOpen: false, householdId: null, householdName: '' })}
        onSubmit={confirmAddMemberToHousehold}
        householdName={addMemberToHouseholdModal.householdName}
      />
    </div>
  );
}

// Add Member to Household Modal Component
function AddMemberToHouseholdModal({ isOpen, onClose, onSubmit, householdName }) {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAvailableUsers();
      setSelectedUserId('');
      setSelectedRole('member');
      setError('');
    }
  }, [isOpen]);

  const loadAvailableUsers = async () => {
    setLoading(true);
    try {
      const response = await authAPI.getAvailableUsers();
      setAvailableUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load available users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAdding(true);

    try {
      if (!selectedUserId) {
        setError('Please select a user');
        setAdding(false);
        return;
      }

      await onSubmit(selectedUserId, selectedRole);
      onClose();
    } catch (err) {
      console.error('Add member error:', err);
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-wrapper">
            <UserPlus size={24} />
            <h3>Add Member to {householdName}</h3>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            {loading ? (
              <div className="loading-state">Loading users...</div>
            ) : availableUsers.length === 0 ? (
              <div className="empty-state">
                <p>No users available to add</p>
                <small>All users are already in a household</small>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="user">Select User</label>
                  <CustomSelect
                    name="user"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={adding}
                    placeholder="Choose a user..."
                    options={availableUsers.map(user => ({
                      value: user.id,
                      label: `${user.first_name} ${user.last_name} (${user.email})`,
                      icon: '👤'
                    }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">Household Role</label>
                  <CustomSelect
                    name="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    disabled={adding}
                    options={[
                      { value: 'member', label: 'Member', icon: '👤' },
                      { value: 'manager', label: 'Manager', icon: '👑' }
                    ]}
                  />
                  <small className="form-hint">
                    Managers can manage household settings and members
                  </small>
                </div>

                <div className="settings-info-box">
                  <h4>ℹ️ About Adding Members</h4>
                  <p>
                    As an admin, you can directly add users to households without their approval.
                    The user will be immediately assigned to the household with the selected role.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={adding}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={adding || loading || availableUsers.length === 0}
            >
              {adding ? 'Adding...' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Security Settings (Password policies, session, MFA)
function SecuritySettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireLowercase: true,
    passwordRequireNumbers: true,
    passwordRequireSpecialChars: false,
    sessionTimeout: 24, // hours
    maxLoginAttempts: 5,
    lockoutDuration: 30, // minutes
    requireEmailVerification: false,
    allowSelfRegistration: false
  });
  const [loading, setLoading] = useState(true);
  const [smtpConfigured, setSmtpConfigured] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
    checkSmtpStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings('authentication');
      setSettings(response.data.setting_value);
    } catch (error) {
      console.error('Failed to load settings:', error);
      showError('Failed to load authentication settings');
    } finally {
      setLoading(false);
    }
  };

  const checkSmtpStatus = async () => {
    try {
      const response = await adminAPI.getSettings('smtp');
      const smtp = response.data.setting_value;
      // SMTP is configured if enabled and has host, auth, and from email
      setSmtpConfigured(smtp?.enabled && smtp?.host && smtp?.authUser && smtp?.fromEmail);
    } catch (error) {
      console.error('Failed to check SMTP status:', error);
      setSmtpConfigured(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const handleToggle = (field) => {
    // Check if trying to enable email verification without SMTP
    if (field === 'requireEmailVerification' && !settings.requireEmailVerification && !smtpConfigured) {
      showError('Email verification requires SMTP to be configured and enabled. Please configure SMTP settings first.');
      return;
    }
    setSettings({
      ...settings,
      [field]: !settings[field]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await adminAPI.updateSettings('authentication', settings);
      success('Authentication settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      showError('Failed to update authentication settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-section">
      <h2>Security Settings</h2>
      <p className="section-description">Configure password policies, session management, and security options</p>
      
      <form onSubmit={handleSubmit} className="auth-settings-form">
        {/* Password Requirements */}
        <div className="settings-group compact-group">
          <h3>Password Requirements</h3>
          <p className="section-hint">Set minimum password complexity requirements for all users</p>
          
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="passwordMinLength">Minimum Length</label>
              <CustomSelect
                name="passwordMinLength"
                value={settings.passwordMinLength.toString()}
                onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value))}
                disabled={loading}
                options={[
                  { value: '6', label: '6 characters', icon: '🔑' },
                  { value: '8', label: '8 characters', icon: '🔑' },
                  { value: '10', label: '10 characters', icon: '🔐' },
                  { value: '12', label: '12 characters', icon: '🔐' },
                  { value: '16', label: '16 characters', icon: '🔐' }
                ]}
              />
            </div>
          </div>

          <div className="toggle-grid">
            <Toggle
              checked={settings.passwordRequireUppercase}
              onChange={() => handleToggle('passwordRequireUppercase')}
              disabled={loading}
              label="Uppercase"
              labelPosition="right"
              size="sm"
            />
            <Toggle
              checked={settings.passwordRequireLowercase}
              onChange={() => handleToggle('passwordRequireLowercase')}
              disabled={loading}
              label="Lowercase"
              labelPosition="right"
              size="sm"
            />
            <Toggle
              checked={settings.passwordRequireNumbers}
              onChange={() => handleToggle('passwordRequireNumbers')}
              disabled={loading}
              label="Numbers"
              labelPosition="right"
              size="sm"
            />
            <Toggle
              checked={settings.passwordRequireSpecialChars}
              onChange={() => handleToggle('passwordRequireSpecialChars')}
              disabled={loading}
              label="Special Characters"
              labelPosition="right"
              size="sm"
            />
          </div>
        </div>

        {/* Session & Security */}
        <div className="settings-group compact-group">
          <h3>Session & Security</h3>
          <p className="section-hint">Configure session timeouts and account protection</p>
          
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="sessionTimeout">Session Timeout</label>
              <CustomSelect
                name="sessionTimeout"
                value={settings.sessionTimeout.toString()}
                onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value))}
                disabled={loading}
                options={[
                  { value: '1', label: '1 hour', icon: '⏰' },
                  { value: '4', label: '4 hours', icon: '⏰' },
                  { value: '8', label: '8 hours', icon: '⏰' },
                  { value: '12', label: '12 hours', icon: '⏰' },
                  { value: '24', label: '24 hours', icon: '📅' },
                  { value: '72', label: '3 days', icon: '📅' },
                  { value: '168', label: '7 days', icon: '📆' }
                ]}
              />
            </div>

            <div className="form-group half-width">
              <label htmlFor="maxLoginAttempts">Max Login Attempts</label>
              <CustomSelect
                name="maxLoginAttempts"
                value={settings.maxLoginAttempts.toString()}
                onChange={(e) => handleChange('maxLoginAttempts', parseInt(e.target.value))}
                disabled={loading}
                options={[
                  { value: '3', label: '3 attempts', icon: '🚫' },
                  { value: '5', label: '5 attempts', icon: '🚫' },
                  { value: '10', label: '10 attempts', icon: '🚫' },
                  { value: '0', label: 'Unlimited', icon: '♾️' }
                ]}
              />
            </div>
          </div>

          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="lockoutDuration">Lockout Duration</label>
              <CustomSelect
                name="lockoutDuration"
                value={settings.lockoutDuration.toString()}
                onChange={(e) => handleChange('lockoutDuration', parseInt(e.target.value))}
                disabled={loading}
                options={[
                  { value: '5', label: '5 minutes', icon: '⏱️' },
                  { value: '15', label: '15 minutes', icon: '⏱️' },
                  { value: '30', label: '30 minutes', icon: '⏱️' },
                  { value: '60', label: '1 hour', icon: '⏰' },
                  { value: '1440', label: '24 hours', icon: '📅' }
                ]}
              />
            </div>
          </div>
        </div>

        {/* Registration */}
        <div className="settings-group compact-group">
          <h3>Registration</h3>
          <p className="section-hint">Control email verification for new users</p>
          
          <div className="toggle-grid">
            <div className="toggle-item-inline">
              <Toggle
                checked={settings.requireEmailVerification}
                onChange={() => handleToggle('requireEmailVerification')}
                disabled={loading || (!smtpConfigured && !settings.requireEmailVerification)}
                label="Email Verification"
                labelPosition="right"
                size="sm"
              />
              {!smtpConfigured && (
                <span className="toggle-requirement" title="SMTP must be configured to enable this feature">
                  <Mail size={14} />
                  <span>Requires SMTP</span>
                </span>
              )}
            </div>
          </div>
          {!smtpConfigured && (
            <div className="settings-warning-box" style={{ marginTop: 'var(--spacing-md)' }}>
              <AlertTriangle size={20} />
              <div>
                <strong>SMTP Required</strong>
                <p>Email verification requires a working SMTP configuration. Go to Email / SMTP settings to configure email delivery.</p>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Single Sign-On Settings (OAuth/SSO)
function SSOSettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    google: {
      enabled: false,
      clientId: '',
      clientSecret: '',
      buttonLabel: 'Google'
    },
    microsoft: {
      enabled: false,
      clientId: '',
      clientSecret: '',
      tenant: 'common',
      buttonLabel: 'Microsoft'
    },
    custom: {
      enabled: false,
      name: '',
      buttonLabel: 'Organization',
      clientId: '',
      clientSecret: '',
      authorizationUrl: '',
      tokenUrl: '',
      userInfoUrl: '',
      scopes: 'openid profile email',
      userIdField: 'sub',
      emailField: 'email',
      firstNameField: 'given_name',
      lastNameField: 'family_name'
    },
    callbackBaseUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [showMicrosoftSecret, setShowMicrosoftSecret] = useState(false);
  const [showCustomSecret, setShowCustomSecret] = useState(false);
  
  // Track expanded state for each provider (collapsed when configured)
  const [expandedProviders, setExpandedProviders] = useState({
    google: false,
    microsoft: false,
    custom: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings('oauth');
      if (response.data.setting_value) {
        // Merge with defaults to ensure all fields exist
        const loaded = response.data.setting_value;
        setSettings(prev => ({
          ...prev,
          ...loaded,
          google: { ...prev.google, ...loaded.google },
          microsoft: { ...prev.microsoft, ...loaded.microsoft },
          custom: { ...prev.custom, ...loaded.custom }
        }));
      }
    } catch (error) {
      console.error('Failed to load OAuth settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider, field, value) => {
    setSettings({
      ...settings,
      [provider]: {
        ...settings[provider],
        [field]: value
      }
    });
  };

  const handleProviderToggle = (provider) => {
    setSettings({
      ...settings,
      [provider]: {
        ...settings[provider],
        enabled: !settings[provider].enabled
      }
    });
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  // Check if a provider is fully configured (can be configured even when disabled)
  const isProviderConfigured = (provider) => {
    const p = settings[provider];
    if (provider === 'google' || provider === 'microsoft') {
      return !!(p?.clientId && p?.clientSecret);
    }
    if (provider === 'custom') {
      return !!(p?.clientId && p?.clientSecret && p?.authorizationUrl && p?.tokenUrl && p?.userInfoUrl);
    }
    return false;
  };

  // Toggle provider expansion
  const toggleProviderExpanded = (provider) => {
    setExpandedProviders(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }));
  };

  // Check if provider form should be shown (only when expanded)
  // eslint-disable-next-line no-unused-vars
  const shouldShowProviderForm = (provider) => {
    return expandedProviders[provider];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAPI.updateSettings('oauth', settings);
      success('OAuth settings saved successfully');
    } catch (error) {
      console.error('Failed to save OAuth settings:', error);
      showError('Failed to save OAuth settings');
    } finally {
      setSaving(false);
    }
  };

  // Google Icon SVG
  const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  // Microsoft Icon SVG
  const MicrosoftIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z"/>
      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
      <path fill="#FFB900" d="M13 13h10v10H13z"/>
    </svg>
  );

  if (loading) {
    return (
      <div className="admin-section">
        <h2>OAuth / SSO Settings</h2>
        <div className="loading-state"><Loader2 size={24} className="spinner" /><span>Loading settings...</span></div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <h2>Single Sign-On</h2>
      <p className="section-description">Configure external identity providers for single sign-on</p>

      <form onSubmit={handleSubmit} className="auth-settings-form">
        {/* Callback URL */}
        <div className="settings-group compact-group">
          <h3>General Settings</h3>
          <p className="section-hint">Configure the callback URL for OAuth providers</p>
          
          <div className="settings-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="callbackBaseUrl">Callback Base URL</label>
              <TextInput
                id="callbackBaseUrl"
                name="callbackBaseUrl"
                value={settings.callbackBaseUrl}
                onChange={(e) => handleChange('callbackBaseUrl', e.target.value)}
                placeholder="https://your-domain.com"
                disabled={saving}
              />
              <small className="form-hint">The base URL where OAuth callbacks will be received. Leave empty to use the current domain.</small>
            </div>
          </div>
        </div>

        {/* Google OAuth */}
        <div className="settings-group compact-group">
          <div 
            className="oauth-provider-header clickable"
            onClick={() => toggleProviderExpanded('google')}
          >
            <div className="oauth-provider-title">
              <GoogleIcon />
              <h3>Google OAuth</h3>
              {isProviderConfigured('google') && (
                <span className="oauth-configured-badge">Configured</span>
              )}
            </div>
            {expandedProviders.google ? <ChevronDown size={18} className="expand-icon" /> : <ChevronRight size={18} className="expand-icon" />}
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                checked={settings.google?.enabled || false}
                onChange={() => handleProviderToggle('google')}
                disabled={saving}
                size="sm"
              />
            </div>
          </div>
          {!expandedProviders.google && (
            <p className="section-hint">Allow users to sign in with their Google account</p>
          )}
          
          <div className={`oauth-provider-content ${expandedProviders.google ? 'expanded' : ''}`}>
            <div>
              <p className="section-hint">Allow users to sign in with their Google account</p>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="googleClientId">Client ID</label>
                  <TextInput
                    id="googleClientId"
                    name="googleClientId"
                    value={settings.google?.clientId || ''}
                    onChange={(e) => handleProviderChange('google', 'clientId', e.target.value)}
                    placeholder="xxxx.apps.googleusercontent.com"
                    disabled={saving}
                  />
                </div>
                <div className="form-group half-width">
                  <label htmlFor="googleClientSecret">Client Secret</label>
                  <div className="password-input-wrapper">
                    <TextInput
                      type={showGoogleSecret ? 'text' : 'password'}
                      id="googleClientSecret"
                      name="googleClientSecret"
                      value={settings.google?.clientSecret || ''}
                      onChange={(e) => handleProviderChange('google', 'clientSecret', e.target.value)}
                      placeholder="••••••••"
                      disabled={saving}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowGoogleSecret(!showGoogleSecret)} disabled={saving}>
                      {showGoogleSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="googleButtonLabel">Button Label</label>
                  <TextInput
                    id="googleButtonLabel"
                    name="googleButtonLabel"
                    value={settings.google?.buttonLabel || ''}
                    onChange={(e) => handleProviderChange('google', 'buttonLabel', e.target.value)}
                    placeholder="Google"
                    disabled={saving}
                  />
                  <small className="form-hint">Displayed as "Continue with {settings.google?.buttonLabel || 'Google'}"</small>
                </div>
              </div>
              <div className="settings-info-box" style={{ marginTop: 'var(--spacing-sm)' }}>
                <h4>📋 Setup Instructions</h4>
                <p>
                  1. Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console</a><br/>
                  2. Create OAuth 2.0 credentials<br/>
                  3. Add authorized redirect URI: <code>{settings.callbackBaseUrl || window.location.origin}/oauth/callback</code>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Microsoft OAuth */}
        <div className="settings-group compact-group">
          <div 
            className="oauth-provider-header clickable"
            onClick={() => toggleProviderExpanded('microsoft')}
          >
            <div className="oauth-provider-title">
              <MicrosoftIcon />
              <h3>Microsoft / Entra ID</h3>
              {isProviderConfigured('microsoft') && (
                <span className="oauth-configured-badge">Configured</span>
              )}
            </div>
            {expandedProviders.microsoft ? <ChevronDown size={18} className="expand-icon" /> : <ChevronRight size={18} className="expand-icon" />}
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                checked={settings.microsoft?.enabled || false}
                onChange={() => handleProviderToggle('microsoft')}
                disabled={saving}
                size="sm"
              />
            </div>
          </div>
          {!expandedProviders.microsoft && (
            <p className="section-hint">Allow users to sign in with their Microsoft account or Entra ID</p>
          )}
          
          <div className={`oauth-provider-content ${expandedProviders.microsoft ? 'expanded' : ''}`}>
            <div>
              <p className="section-hint">Allow users to sign in with their Microsoft account or Entra ID</p>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="microsoftClientId">Application (Client) ID</label>
                  <TextInput
                    id="microsoftClientId"
                    name="microsoftClientId"
                    value={settings.microsoft?.clientId || ''}
                    onChange={(e) => handleProviderChange('microsoft', 'clientId', e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    disabled={saving}
                  />
                </div>
                <div className="form-group half-width">
                  <label htmlFor="microsoftClientSecret">Client Secret</label>
                  <div className="password-input-wrapper">
                    <TextInput
                      type={showMicrosoftSecret ? 'text' : 'password'}
                      id="microsoftClientSecret"
                      name="microsoftClientSecret"
                      value={settings.microsoft?.clientSecret || ''}
                      onChange={(e) => handleProviderChange('microsoft', 'clientSecret', e.target.value)}
                      placeholder="••••••••"
                      disabled={saving}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowMicrosoftSecret(!showMicrosoftSecret)} disabled={saving}>
                      {showMicrosoftSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="microsoftTenant">Tenant</label>
                  <CustomSelect
                    name="microsoftTenant"
                    value={['common', 'organizations', 'consumers'].includes(settings.microsoft?.tenant) ? settings.microsoft?.tenant : 'custom'}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        handleProviderChange('microsoft', 'tenant', '');
                      } else {
                        handleProviderChange('microsoft', 'tenant', e.target.value);
                      }
                    }}
                    disabled={saving}
                    options={[
                      { value: 'common', label: 'Common (All Accounts)', icon: '🌐' },
                      { value: 'organizations', label: 'Organizations Only', icon: '🏢' },
                      { value: 'consumers', label: 'Personal Accounts Only', icon: '👤' },
                      { value: 'custom', label: 'Custom Tenant ID', icon: '🔒' }
                    ]}
                  />
                </div>
                {!['common', 'organizations', 'consumers'].includes(settings.microsoft?.tenant) && (
                  <div className="form-group half-width">
                    <label htmlFor="microsoftTenantId">Tenant ID</label>
                    <TextInput
                      id="microsoftTenantId"
                      name="microsoftTenantId"
                      value={settings.microsoft?.tenant || ''}
                      onChange={(e) => handleProviderChange('microsoft', 'tenant', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      disabled={saving}
                    />
                  </div>
                )}
              </div>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="microsoftButtonLabel">Button Label</label>
                  <TextInput
                    id="microsoftButtonLabel"
                    name="microsoftButtonLabel"
                    value={settings.microsoft?.buttonLabel || ''}
                    onChange={(e) => handleProviderChange('microsoft', 'buttonLabel', e.target.value)}
                    placeholder="Microsoft"
                    disabled={saving}
                  />
                  <small className="form-hint">Displayed as "Continue with {settings.microsoft?.buttonLabel || 'Microsoft'}"</small>
                </div>
              </div>
              <div className="settings-info-box" style={{ marginTop: 'var(--spacing-sm)' }}>
                <h4>📋 Setup Instructions</h4>
                <p>
                  1. Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps" target="_blank" rel="noopener noreferrer">Azure Portal - App Registrations</a><br/>
                  2. Register a new application<br/>
                  3. Add redirect URI: <code>{settings.callbackBaseUrl || window.location.origin}/oauth/callback</code><br/>
                  4. Create a client secret under "Certificates & secrets"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom OAuth / OIDC Provider */}
        <div className="settings-group compact-group">
          <div 
            className="oauth-provider-header clickable"
            onClick={() => toggleProviderExpanded('custom')}
          >
            <div className="oauth-provider-title">
              <Key size={20} />
              <h3>Custom OAuth / OIDC</h3>
              {isProviderConfigured('custom') && (
                <span className="oauth-configured-badge">Configured</span>
              )}
            </div>
            {expandedProviders.custom ? <ChevronDown size={18} className="expand-icon" /> : <ChevronRight size={18} className="expand-icon" />}
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                checked={settings.custom?.enabled || false}
                onChange={() => handleProviderToggle('custom')}
                disabled={saving}
                size="sm"
              />
            </div>
          </div>
          {!expandedProviders.custom && (
            <p className="section-hint">Configure a custom OAuth 2.0 or OIDC provider (Keycloak, Authentik, Okta, etc.)</p>
          )}
          
          <div className={`oauth-provider-content ${expandedProviders.custom ? 'expanded' : ''}`}>
            <div>
              <p className="section-hint">Configure a custom OAuth 2.0 or OIDC provider (Keycloak, Authentik, Okta, etc.)</p>
              {/* Basic Settings */}
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="customName">Provider Name</label>
                  <TextInput
                    id="customName"
                    name="customName"
                    value={settings.custom?.name || ''}
                    onChange={(e) => handleProviderChange('custom', 'name', e.target.value)}
                    placeholder="Keycloak"
                    disabled={saving}
                  />
                </div>
                <div className="form-group half-width">
                  <label htmlFor="customButtonLabel">Button Label</label>
                  <TextInput
                    id="customButtonLabel"
                    name="customButtonLabel"
                    value={settings.custom?.buttonLabel || ''}
                    onChange={(e) => handleProviderChange('custom', 'buttonLabel', e.target.value)}
                    placeholder="Organization"
                    disabled={saving}
                  />
                  <small className="form-hint">Displayed as "Continue with {settings.custom?.buttonLabel || 'Organization'}"</small>
                </div>
              </div>

              {/* Client Credentials */}
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="customClientId">Client ID</label>
                  <TextInput
                    id="customClientId"
                    name="customClientId"
                    value={settings.custom?.clientId || ''}
                    onChange={(e) => handleProviderChange('custom', 'clientId', e.target.value)}
                    placeholder="your-client-id"
                    disabled={saving}
                  />
                </div>
                <div className="form-group half-width">
                  <label htmlFor="customClientSecret">Client Secret</label>
                  <div className="password-input-wrapper">
                    <TextInput
                      type={showCustomSecret ? 'text' : 'password'}
                      id="customClientSecret"
                      name="customClientSecret"
                      value={settings.custom?.clientSecret || ''}
                      onChange={(e) => handleProviderChange('custom', 'clientSecret', e.target.value)}
                      placeholder="••••••••"
                      disabled={saving}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowCustomSecret(!showCustomSecret)} disabled={saving}>
                      {showCustomSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* OAuth Endpoints */}
              <div className="settings-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="customAuthorizationUrl">Authorization URL</label>
                  <TextInput
                    id="customAuthorizationUrl"
                    name="customAuthorizationUrl"
                    value={settings.custom?.authorizationUrl || ''}
                    onChange={(e) => handleProviderChange('custom', 'authorizationUrl', e.target.value)}
                    placeholder="https://idp.example.com/oauth/authorize"
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="settings-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="customTokenUrl">Token URL</label>
                  <TextInput
                    id="customTokenUrl"
                    name="customTokenUrl"
                    value={settings.custom?.tokenUrl || ''}
                    onChange={(e) => handleProviderChange('custom', 'tokenUrl', e.target.value)}
                    placeholder="https://idp.example.com/oauth/token"
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="settings-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="customUserInfoUrl">User Info URL</label>
                  <TextInput
                    id="customUserInfoUrl"
                    name="customUserInfoUrl"
                    value={settings.custom?.userInfoUrl || ''}
                    onChange={(e) => handleProviderChange('custom', 'userInfoUrl', e.target.value)}
                    placeholder="https://idp.example.com/oauth/userinfo"
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="customScopes">Scopes</label>
                  <TextInput
                    id="customScopes"
                    name="customScopes"
                    value={settings.custom?.scopes || ''}
                    onChange={(e) => handleProviderChange('custom', 'scopes', e.target.value)}
                    placeholder="openid profile email"
                    disabled={saving}
                  />
                  <small className="form-hint">Space-separated list of OAuth scopes</small>
                </div>
              </div>

              {/* Field Mappings */}
              <div className="settings-subsection">
                <h4>User Info Field Mappings</h4>
                <p className="section-hint">Map the fields from your provider's user info response to Remidy user fields. Supports dot notation for nested fields (e.g., user.profile.email).</p>
              </div>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="customUserIdField">User ID Field</label>
                  <TextInput
                    id="customUserIdField"
                    name="customUserIdField"
                    value={settings.custom?.userIdField || ''}
                    onChange={(e) => handleProviderChange('custom', 'userIdField', e.target.value)}
                    placeholder="sub"
                    disabled={saving}
                  />
                </div>
                <div className="form-group half-width">
                  <label htmlFor="customEmailField">Email Field</label>
                  <TextInput
                    id="customEmailField"
                    name="customEmailField"
                    value={settings.custom?.emailField || ''}
                    onChange={(e) => handleProviderChange('custom', 'emailField', e.target.value)}
                    placeholder="email"
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="settings-row">
                <div className="form-group half-width">
                  <label htmlFor="customFirstNameField">First Name Field</label>
                  <TextInput
                    id="customFirstNameField"
                    name="customFirstNameField"
                    value={settings.custom?.firstNameField || ''}
                    onChange={(e) => handleProviderChange('custom', 'firstNameField', e.target.value)}
                    placeholder="given_name"
                    disabled={saving}
                  />
                </div>
                <div className="form-group half-width">
                  <label htmlFor="customLastNameField">Last Name Field</label>
                  <TextInput
                    id="customLastNameField"
                    name="customLastNameField"
                    value={settings.custom?.lastNameField || ''}
                    onChange={(e) => handleProviderChange('custom', 'lastNameField', e.target.value)}
                    placeholder="family_name"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="settings-info-box" style={{ marginTop: 'var(--spacing-sm)' }}>
                <h4>📋 Setup Instructions</h4>
                <p>
                  1. Create an OAuth/OIDC application in your identity provider<br/>
                  2. Set the redirect URI to: <code>{settings.callbackBaseUrl || window.location.origin}/oauth/callback</code><br/>
                  3. Copy the authorization, token, and user info endpoints from your provider's documentation<br/>
                  4. Configure field mappings to match your provider's user info response format
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? <><Loader2 size={16} className="spinner" /> Saving...</> : 'Save OAuth Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Application Settings (URL, localization, branding, integrations)
function ApplicationSettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    supportEmail: 'support@example.com',
    timezone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    appUrl: '',
    basePath: '',
    trustProxy: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings('system');
      const loadedSettings = response.data.setting_value;
      setSettings(prev => ({
        ...prev,
        ...loadedSettings
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleToggle = (field) => {
    setSettings({ ...settings, [field]: !settings[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.updateSettings('system', settings);
      success('Application settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      showError('Failed to update application settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-section">
      <h2>Application Settings</h2>
      <p className="section-description">Configure URL, localization, and branding options</p>
      
      <form onSubmit={handleSubmit} className="auth-settings-form">
        {/* URL Configuration */}
        <div className="settings-group compact-group">
          <h3><Globe size={18} /> URL Configuration</h3>
          <p className="section-hint">Configure application URL for reverse proxy and subpath deployments</p>
          
          <div className="settings-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="appUrl">Application URL</label>
              <TextInput
                id="appUrl"
                name="appUrl"
                value={settings.appUrl || ''}
                onChange={(e) => handleChange('appUrl', e.target.value)}
                placeholder="https://example.com"
                disabled={loading}
              />
              <small className="form-hint">The public-facing URL of your application. Used for generating links in emails, OAuth callbacks, etc.</small>
            </div>
          </div>

          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="basePath">Base Path</label>
              <TextInput
                id="basePath"
                name="basePath"
                value={settings.basePath || ''}
                onChange={(e) => handleChange('basePath', e.target.value)}
                placeholder="/remidy"
                disabled={loading}
              />
              <small className="form-hint">Set this if running at a subpath (e.g., /remidy). Leave empty for root deployment.</small>
            </div>
            <div className="form-group half-width">
              <div className="toggle-item-inline" style={{ marginTop: '1.75rem' }}>
                <Toggle
                  checked={settings.trustProxy || false}
                  onChange={() => handleToggle('trustProxy')}
                  disabled={loading}
                  label="Trust Proxy Headers"
                  labelPosition="right"
                  size="sm"
                />
              </div>
              <small className="form-hint">Enable if behind a reverse proxy (nginx, Traefik, etc.)</small>
            </div>
          </div>

          {(settings.appUrl || settings.basePath) && (
            <div className="settings-info-box" style={{ marginTop: 'var(--spacing-sm)' }}>
              <h4><Link size={16} /> Configured URL</h4>
              <p>
                Your application will be accessible at: <code>{settings.appUrl || window.location.origin}{settings.basePath || ''}</code>
              </p>
            </div>
          )}
        </div>

        <div className="settings-group compact-group">
          <h3>Localization</h3>
          <p className="section-hint">Configure regional settings</p>
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="timezone">Timezone</label>
              <CustomSelect
                name="timezone"
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                disabled={loading}
                options={[
                  { value: 'America/New_York', label: 'Eastern Time (GMT-05:00 / GMT-04:00)', icon: '🌎' },
                  { value: 'America/Chicago', label: 'Central Time (GMT-06:00 / GMT-05:00)', icon: '🌎' },
                  { value: 'America/Denver', label: 'Mountain Time (GMT-07:00 / GMT-06:00)', icon: '🌎' },
                  { value: 'America/Los_Angeles', label: 'Pacific Time (GMT-08:00 / GMT-07:00)', icon: '🌎' },
                  { value: 'America/Anchorage', label: 'Alaska Time (GMT-09:00 / GMT-08:00)', icon: '🌎' },
                  { value: 'Pacific/Honolulu', label: 'Hawaii Time (GMT-10:00)', icon: '🌴' },
                  { value: 'UTC', label: 'UTC (GMT+00:00)', icon: '🌐' }
                ]}
              />
            </div>
            <div className="form-group half-width">
              <label htmlFor="dateFormat">Date Format</label>
              <CustomSelect
                name="dateFormat"
                value={settings.dateFormat}
                onChange={(e) => handleChange('dateFormat', e.target.value)}
                disabled={loading}
                options={[
                  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', icon: '📅' },
                  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', icon: '📅' },
                  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', icon: '📅' }
                ]}
              />
            </div>
          </div>
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="currency">Currency</label>
              <CustomSelect
                name="currency"
                value={settings.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                disabled={loading}
                options={[
                  { value: 'USD', label: 'USD ($)', icon: '💵' },
                  { value: 'EUR', label: 'EUR (€)', icon: '💶' },
                  { value: 'GBP', label: 'GBP (£)', icon: '💷' }
                ]}
              />
            </div>
          </div>
        </div>

        <div className="settings-group compact-group">
          <h3>Support</h3>
          <p className="section-hint">Contact information for user support</p>
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="supportEmail">Support Email</label>
              <TextInput
                type="email"
                id="supportEmail"
                name="supportEmail"
                value={settings.supportEmail}
                onChange={(e) => handleChange('supportEmail', e.target.value)}
                placeholder="support@example.com"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Settings'}</button>
        </div>
      </form>
    </div>
  );
}

// Integrations Settings (Third-party API integrations)
function IntegrationsSettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    logoDevSecretKey: '',
    logoDevPublishableKey: ''
  });
  const [loading, setLoading] = useState(true);
  const [showLogoDevKey, setShowLogoDevKey] = useState(false);
  const [showLogoDevPubKey, setShowLogoDevPubKey] = useState(false);
  const [testingLogoApi, setTestingLogoApi] = useState(false);
  const [logoApiStatus, setLogoApiStatus] = useState(null); // null, 'success', 'error'

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings('system');
      const loadedSettings = response.data.setting_value;
      setSettings(prev => ({
        ...prev,
        logoDevSecretKey: loadedSettings.logoDevSecretKey || '',
        logoDevPublishableKey: loadedSettings.logoDevPublishableKey || ''
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
    // Reset logo API status when key changes
    if (field === 'logoDevSecretKey' || field === 'logoDevPublishableKey') {
      setLogoApiStatus(null);
    }
  };

  const handleTestLogoApi = async () => {
    if (!settings.logoDevSecretKey || !settings.logoDevPublishableKey) {
      showError('Please enter both Logo.dev keys first');
      return;
    }
    setTestingLogoApi(true);
    setLogoApiStatus(null);
    try {
      // Save the keys first, then test
      const systemResponse = await adminAPI.getSettings('system');
      const systemSettings = systemResponse.data.setting_value || {};
      await adminAPI.updateSettings('system', {
        ...systemSettings,
        logoDevSecretKey: settings.logoDevSecretKey,
        logoDevPublishableKey: settings.logoDevPublishableKey
      });
      const response = await adminAPI.testLogoApi();
      if (response.data.success) {
        setLogoApiStatus('success');
        success('Logo.dev API connection verified!');
      } else {
        setLogoApiStatus('error');
        showError(response.data.message || 'API test failed');
      }
    } catch (error) {
      setLogoApiStatus('error');
      showError(error.response?.data?.message || 'Failed to test Logo.dev API');
    } finally {
      setTestingLogoApi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Get current system settings and merge in integrations
      const systemResponse = await adminAPI.getSettings('system');
      const systemSettings = systemResponse.data.setting_value || {};
      await adminAPI.updateSettings('system', {
        ...systemSettings,
        logoDevSecretKey: settings.logoDevSecretKey,
        logoDevPublishableKey: settings.logoDevPublishableKey
      });
      success('Integration settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      showError('Failed to update integration settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-section">
      <h2>Integrations</h2>
      <p className="section-description">Configure third-party service integrations</p>
      
      <form onSubmit={handleSubmit} className="auth-settings-form">
        {/* Logo.dev API Integration */}
        <div className="settings-group compact-group">
          <h3><Image size={18} /> Company Logo API</h3>
          <p className="section-hint">Configure Logo.dev for automatic company logo fetching when creating bills</p>
          
          <div className="settings-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="logoDevPublishableKey">Logo.dev Publishable Key</label>
              <div className="password-input-wrapper">
                <TextInput
                  type={showLogoDevPubKey ? 'text' : 'password'}
                  id="logoDevPublishableKey"
                  name="logoDevPublishableKey"
                  value={settings.logoDevPublishableKey || ''}
                  onChange={(e) => handleChange('logoDevPublishableKey', e.target.value)}
                  placeholder="pk_xxxxxxxxxxxx"
                  disabled={loading}
                />
                <button 
                  type="button" 
                  className="password-toggle" 
                  onClick={() => setShowLogoDevPubKey(!showLogoDevPubKey)} 
                  disabled={loading}
                  title={showLogoDevPubKey ? 'Hide key' : 'Show key'}
                >
                  {showLogoDevPubKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <small className="form-hint">Used for fetching logo images (starts with <code>pk_</code>)</small>
            </div>
          </div>

          <div className="settings-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="logoDevSecretKey">Logo.dev Secret Key</label>
              <div className="password-input-wrapper">
                <TextInput
                  type={showLogoDevKey ? 'text' : 'password'}
                  id="logoDevSecretKey"
                  name="logoDevSecretKey"
                  value={settings.logoDevSecretKey || ''}
                  onChange={(e) => handleChange('logoDevSecretKey', e.target.value)}
                  placeholder="sk_xxxxxxxxxxxx"
                  disabled={loading}
                />
                <button 
                  type="button" 
                  className="password-toggle" 
                  onClick={() => setShowLogoDevKey(!showLogoDevKey)} 
                  disabled={loading}
                  title={showLogoDevKey ? 'Hide key' : 'Show key'}
                >
                  {showLogoDevKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <small className="form-hint">Used for brand search API (starts with <code>sk_</code>)</small>
            </div>
          </div>

          <div className="settings-row" style={{ marginTop: 'var(--spacing-sm)' }}>
            <LoadingButton
              type="button"
              loading={testingLogoApi}
              className={`btn-secondary ${logoApiStatus === 'success' ? 'btn-success-outline' : logoApiStatus === 'error' ? 'btn-danger-outline' : ''}`}
              onClick={handleTestLogoApi}
              disabled={!settings.logoDevSecretKey || !settings.logoDevPublishableKey || loading}
              style={{ minWidth: '140px' }}
            >
              {logoApiStatus === 'success' ? (
                <><CheckCircle size={16} /> Verified</>
              ) : logoApiStatus === 'error' ? (
                <><XCircle size={16} /> Failed</>
              ) : (
                <><Image size={16} /> Test API</>
              )}
            </LoadingButton>
          </div>

          <div className="settings-info-box" style={{ marginTop: 'var(--spacing-md)' }}>
            <h4>📋 Setup Instructions</h4>
            <p>
              1. Sign up for a free account at <a href="https://logo.dev" target="_blank" rel="noopener noreferrer">logo.dev</a> <ExternalLink size={12} style={{ verticalAlign: 'middle' }} /><br/>
              2. Go to your dashboard and copy your publishable key (starts with <code>pk_</code>)<br/>
              3. Create a secret key (starts with <code>sk_</code>)<br/>
              4. Paste both keys above and click "Test API" to verify
            </p>
          </div>

          {(!settings.logoDevSecretKey || !settings.logoDevPublishableKey) && (
            <div className="settings-info-box" style={{ marginTop: 'var(--spacing-sm)', backgroundColor: 'var(--background-color)' }}>
              <h4>ℹ️ Without API Keys</h4>
              <p>
                The application will still attempt to fetch logos using domain guessing (e.g., "Netflix" → netflix.com). 
                Adding Logo.dev keys enables smarter company name search for better logo matching.
              </p>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Settings'}</button>
        </div>
      </form>
    </div>
  );
}

// Maintenance Settings (maintenance mode, data retention)
function MaintenanceSettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    maintenanceMessage: 'The application is currently under maintenance. Please check back soon.',
    dataRetention: {
      paymentHistoryRetention: 'forever',
      notificationRetention: '90',
      sessionRetention: '30',
      inactiveBillRetention: 'forever'
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings('system');
      const loadedSettings = response.data.setting_value;
      setSettings(prev => ({
        ...prev,
        maintenanceMode: loadedSettings.maintenanceMode || false,
        maintenanceMessage: loadedSettings.maintenanceMessage || prev.maintenanceMessage,
        dataRetention: {
          ...prev.dataRetention,
          ...loadedSettings.dataRetention
        }
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleRetentionChange = (field, value) => {
    setSettings({
      ...settings,
      dataRetention: {
        ...settings.dataRetention,
        [field]: value
      }
    });
  };

  const handleToggle = (field) => {
    setSettings({ ...settings, [field]: !settings[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Get current system settings and merge
      const currentResponse = await adminAPI.getSettings('system');
      const currentSettings = currentResponse.data.setting_value;
      const mergedSettings = {
        ...currentSettings,
        maintenanceMode: settings.maintenanceMode,
        maintenanceMessage: settings.maintenanceMessage,
        dataRetention: settings.dataRetention
      };
      await adminAPI.updateSettings('system', mergedSettings);
      success('Maintenance settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      showError('Failed to update maintenance settings');
    } finally {
      setLoading(false);
    }
  };

  const retentionOptions = [
    { value: '30', label: '30 days', icon: '📅' },
    { value: '90', label: '90 days', icon: '📅' },
    { value: '180', label: '6 months', icon: '📆' },
    { value: '365', label: '1 year', icon: '📆' },
    { value: '730', label: '2 years', icon: '📆' },
    { value: 'forever', label: 'Keep forever', icon: '♾️' }
  ];

  const sessionRetentionOptions = [
    { value: '7', label: '7 days', icon: '📅' },
    { value: '14', label: '14 days', icon: '📅' },
    { value: '30', label: '30 days', icon: '📅' },
    { value: '60', label: '60 days', icon: '📆' },
    { value: '90', label: '90 days', icon: '📆' }
  ];

  const notificationRetentionOptions = [
    { value: '7', label: '7 days', icon: '📅' },
    { value: '30', label: '30 days', icon: '📅' },
    { value: '60', label: '60 days', icon: '📅' },
    { value: '90', label: '90 days', icon: '📆' },
    { value: '180', label: '6 months', icon: '📆' },
    { value: 'forever', label: 'Keep forever', icon: '♾️' }
  ];

  return (
    <div className="admin-section">
      <h2>Maintenance</h2>
      <p className="section-description">Maintenance mode and data retention policies</p>
      
      <form onSubmit={handleSubmit} className="auth-settings-form">
        {/* Maintenance Mode */}
        <div className="settings-group compact-group">
          <h3><AlertTriangle size={18} /> Maintenance Mode</h3>
          <p className="section-hint">Temporarily disable access for non-administrators</p>
          
          <div className="toggle-grid">
            <Toggle
              checked={settings.maintenanceMode}
              onChange={() => handleToggle('maintenanceMode')}
              disabled={loading}
              label="Enable Maintenance Mode"
              labelPosition="right"
              size="sm"
            />
          </div>
          
          {settings.maintenanceMode && (
            <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
              <label htmlFor="maintenanceMessage">Maintenance Message</label>
              <textarea
                id="maintenanceMessage"
                value={settings.maintenanceMessage}
                onChange={(e) => handleChange('maintenanceMessage', e.target.value)}
                disabled={loading}
                className="textarea-input"
                rows={3}
                placeholder="Enter a message to display during maintenance..."
              />
            </div>
          )}

          {settings.maintenanceMode && (
            <div className="settings-warning-box" style={{ marginTop: 'var(--spacing-md)' }}>
              <AlertTriangle size={20} />
              <div>
                <strong>Maintenance Mode Active</strong>
                <p>Only administrators can access the application. All other users will see the maintenance message.</p>
              </div>
            </div>
          )}
        </div>

        {/* Data Retention Settings */}
        <div className="settings-group compact-group">
          <h3><Clock size={18} /> Data Retention</h3>
          <p className="section-hint">Configure how long data is kept before automatic cleanup. Cleanup runs daily at 2:00 AM.</p>
          
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="paymentHistoryRetention">Payment History</label>
              <CustomSelect
                name="paymentHistoryRetention"
                value={settings.dataRetention?.paymentHistoryRetention || 'forever'}
                onChange={(e) => handleRetentionChange('paymentHistoryRetention', e.target.value)}
                disabled={loading}
                options={retentionOptions}
              />
              <small className="form-hint">How long to keep payment records</small>
            </div>
            <div className="form-group half-width">
              <label htmlFor="notificationRetention">Read Notifications</label>
              <CustomSelect
                name="notificationRetention"
                value={settings.dataRetention?.notificationRetention || '90'}
                onChange={(e) => handleRetentionChange('notificationRetention', e.target.value)}
                disabled={loading}
                options={notificationRetentionOptions}
              />
              <small className="form-hint">Delete read notifications after</small>
            </div>
          </div>
          
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="sessionRetention">Expired Sessions</label>
              <CustomSelect
                name="sessionRetention"
                value={settings.dataRetention?.sessionRetention || '30'}
                onChange={(e) => handleRetentionChange('sessionRetention', e.target.value)}
                disabled={loading}
                options={sessionRetentionOptions}
              />
              <small className="form-hint">Keep expired session records for</small>
            </div>
            <div className="form-group half-width">
              <label htmlFor="inactiveBillRetention">Deactivated Bills</label>
              <CustomSelect
                name="inactiveBillRetention"
                value={settings.dataRetention?.inactiveBillRetention || 'forever'}
                onChange={(e) => handleRetentionChange('inactiveBillRetention', e.target.value)}
                disabled={loading}
                options={retentionOptions}
              />
              <small className="form-hint">Auto-delete inactive bills after</small>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Settings'}</button>
        </div>
      </form>
    </div>
  );
}

// Database Management
function DatabaseManagement() {
  const { success, error: showError } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupInProgress, setBackupInProgress] = useState(false);

  useEffect(() => {
    loadDatabaseStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDatabaseStats = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getDatabaseStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load database stats:', error);
      showError('Failed to load database statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    setBackupInProgress(true);
    try {
      await adminAPI.createBackup();
      success('Database backup created successfully');
      // Reload stats to update last backup time
      loadDatabaseStats();
    } catch (error) {
      console.error('Backup failed:', error);
      showError('Failed to create database backup');
    } finally {
      setBackupInProgress(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <div>
          <h2>Database Management</h2>
          <p className="section-description">Monitor database health and perform maintenance operations</p>
        </div>
        <button 
          className="btn-primary" 
          onClick={handleBackup}
          disabled={backupInProgress || loading}
        >
          <Database size={18} />
          {backupInProgress ? <span>Creating...</span> : <span>Create Backup</span>}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading database statistics...</div>
      ) : stats ? (
        <div className="database-stats">
          {/* Database Health */}
          <div className="stats-card">
            <div className="stats-card-header">
              <h3>Database Health</h3>
              <span className={`health-badge ${stats.health.status}`}>
                {stats.health.status === 'healthy' ? '✓ Healthy' : '⚠ Issues Detected'}
              </span>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <label>Status</label>
                <span className="stat-value">{stats.health.status}</span>
              </div>
              <div className="stat-item">
                <label>Connection</label>
                <span className="stat-value">{stats.health.connection}</span>
              </div>
              <div className="stat-item">
                <label>Version</label>
                <span className="stat-value">{stats.health.version}</span>
              </div>
              <div className="stat-item">
                <label>Uptime</label>
                <span className="stat-value">{stats.health.uptime}</span>
              </div>
            </div>
          </div>

          {/* Database Size */}
          <div className="stats-card">
            <div className="stats-card-header">
              <h3>Database Size</h3>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <label>Total Size</label>
                <span className="stat-value">{formatBytes(stats.size.total)}</span>
              </div>
              <div className="stat-item">
                <label>Tables</label>
                <span className="stat-value">{stats.size.tables}</span>
              </div>
              <div className="stat-item">
                <label>Indexes</label>
                <span className="stat-value">{formatBytes(stats.size.indexes)}</span>
              </div>
            </div>
          </div>

          {/* Table Statistics */}
          <div className="stats-card">
            <div className="stats-card-header">
              <h3>Table Statistics</h3>
            </div>
            <div className="table-stats">
              <table>
                <thead>
                  <tr>
                    <th>Table Name</th>
                    <th>Rows</th>
                    <th>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.tables.map(table => (
                    <tr key={table.name}>
                      <td>{table.name}</td>
                      <td>{table.rows.toLocaleString()}</td>
                      <td>{formatBytes(table.size)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Backup Information */}
          <div className="stats-card">
            <div className="stats-card-header">
              <h3>Backup Information</h3>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <label>Last Backup</label>
                <span className="stat-value">{formatDate(stats.backup.lastBackup)}</span>
              </div>
              <div className="stat-item">
                <label>Backup Count</label>
                <span className="stat-value">{stats.backup.count}</span>
              </div>
              <div className="stat-item">
                <label>Total Backup Size</label>
                <span className="stat-value">{formatBytes(stats.backup.totalSize)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <Database size={48} />
          <h3>No Statistics Available</h3>
          <p>Unable to load database statistics</p>
        </div>
      )}
    </div>
  );
}

// Email / SMTP Settings
function EmailSettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    enabled: false,
    host: '',
    port: 587,
    secure: false,
    authUser: '',
    authPass: '',
    fromEmail: '',
    fromName: 'Remidy'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [hasExistingPassword, setHasExistingPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings('smtp');
      if (response.data.setting_value) {
        const loadedSettings = { ...response.data.setting_value };
        // Track if a password exists on the server (it comes back masked)
        const passwordExists = loadedSettings.authPass === '********';
        setHasExistingPassword(passwordExists);
        // Clear the masked password from state - use empty string instead
        // The placeholder will indicate a password is saved
        if (passwordExists) {
          loadedSettings.authPass = '';
        }
        setSettings(loadedSettings);
        setPasswordTouched(false);
      }
    } catch (error) {
      console.error('Failed to load SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'authPass') {
      setPasswordTouched(true);
    }
    setSettings({ ...settings, [field]: value });
  };

  const handleToggle = (field) => {
    setSettings({ ...settings, [field]: !settings[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // If user didn't change the password, send the mask so backend preserves existing
      const settingsToSave = { ...settings };
      if (!passwordTouched && hasExistingPassword) {
        settingsToSave.authPass = '********';
      }
      await adminAPI.updateSettings('smtp', settingsToSave);
      success('SMTP settings saved successfully');
      // After save, reset password tracking state
      if (passwordTouched && settingsToSave.authPass) {
        setHasExistingPassword(true);
        setPasswordTouched(false);
        setSettings(prev => ({ ...prev, authPass: '' }));
      }
    } catch (error) {
      console.error('Failed to save SMTP settings:', error);
      showError('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const settingsToSave = { ...settings };
      if (!passwordTouched && hasExistingPassword) {
        settingsToSave.authPass = '********';
      }
      await adminAPI.updateSettings('smtp', settingsToSave);
      const response = await adminAPI.testSmtpConnection();
      if (response.data.success) success('SMTP connection verified!');
      else showError(response.data.message || 'Connection test failed');
    } catch (error) {
      showError(error.response?.data?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) { showError('Please enter an email address'); return; }
    setSendingTest(true);
    try {
      const settingsToSave = { ...settings };
      if (!passwordTouched && hasExistingPassword) {
        settingsToSave.authPass = '********';
      }
      await adminAPI.updateSettings('smtp', settingsToSave);
      const response = await adminAPI.sendTestEmail(testEmail);
      if (response.data.success) { success(`Test email sent to ${testEmail}`); setTestEmail(''); }
      else showError(response.data.message || 'Failed to send test email');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-section">
        <h2>Email / SMTP Settings</h2>
        <div className="loading-state"><Loader2 size={24} className="spinner" /><span>Loading settings...</span></div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <h2>Email / SMTP Settings</h2>
      <p className="section-description">Configure email delivery for notifications, reminders, and invitations</p>

      <form onSubmit={handleSubmit} className="auth-settings-form">
        {/* Email Service Status Card */}
        <div 
          className={`email-service-card ${settings.enabled ? 'enabled' : 'disabled'}`}
          onClick={() => !saving && handleToggle('enabled')}
        >
          <div className="email-service-icon">
            {settings.enabled ? <Mail size={32} /> : <AlertTriangle size={32} />}
          </div>
          <div className="email-service-info">
            <h3>Email Service</h3>
            <p>{settings.enabled 
              ? 'Email notifications, reminders, and invitations are active' 
              : 'Email functionality is currently disabled'}
            </p>
          </div>
          <div className="email-service-toggle">
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                checked={settings.enabled}
                onChange={() => handleToggle('enabled')}
                disabled={saving}
                size="md"
              />
            </div>
            <span className={`status-label ${settings.enabled ? 'active' : 'inactive'}`}>
              {settings.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>

        {/* SMTP Server Configuration - Combined with Auth and Sender */}
        <div className="settings-group compact-group">
          <h3><Server size={18} /> SMTP Configuration</h3>
          <p className="section-hint">Server connection, authentication, and sender details</p>
          
          {/* Server Settings */}
          <div className="settings-row">
            <div className="form-group flex-2">
              <label htmlFor="host">SMTP Host</label>
              <TextInput
                id="host"
                name="host"
                value={settings.host}
                onChange={(e) => handleChange('host', e.target.value)}
                placeholder="smtp.example.com"
                disabled={saving || !settings.enabled}
              />
            </div>
            <div className="form-group flex-1">
              <label htmlFor="port">Port</label>
              <CustomSelect name="port" value={settings.port.toString()} onChange={(e) => handleChange('port', parseInt(e.target.value))} disabled={saving || !settings.enabled}
                options={[
                  { value: '25', label: '25 (SMTP)', icon: '📤' },
                  { value: '465', label: '465 (SMTPS)', icon: '🔒' },
                  { value: '587', label: '587 (Submission)', icon: '📧' },
                  { value: '2525', label: '2525 (Alt)', icon: '📮' }
                ]} />
            </div>
          </div>

          <div className="tls-toggle-row">
            <Toggle
              checked={settings.secure}
              onChange={() => handleToggle('secure')}
              disabled={saving || !settings.enabled}
              label="Use TLS/SSL encryption (required for port 465)"
              labelPosition="right"
              size="sm"
            />
          </div>

          {/* Authentication */}
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="authUser">Username / Email</label>
              <TextInput
                id="authUser"
                name="authUser"
                value={settings.authUser}
                onChange={(e) => handleChange('authUser', e.target.value)}
                placeholder="user@example.com"
                disabled={saving || !settings.enabled}
                autoComplete="username"
              />
            </div>
            <div className="form-group half-width">
              <label htmlFor="authPass">Password / App Password</label>
              <div className="password-input-wrapper">
                <TextInput
                  type={showPassword ? 'text' : 'password'}
                  id="authPass"
                  name="authPass"
                  value={settings.authPass}
                  onChange={(e) => handleChange('authPass', e.target.value)}
                  placeholder={hasExistingPassword ? 'Password saved — leave blank to keep' : '••••••••'}
                  disabled={saving || !settings.enabled}
                  autoComplete="current-password"
                />
                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} disabled={saving || !settings.enabled} title={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          {/* Sender Info */}
          <div className="settings-row">
            <div className="form-group half-width">
              <label htmlFor="fromName">From Name</label>
              <TextInput
                id="fromName"
                name="fromName"
                value={settings.fromName}
                onChange={(e) => handleChange('fromName', e.target.value)}
                placeholder="Remidy"
                disabled={saving || !settings.enabled}
              />
            </div>
            <div className="form-group half-width">
              <label htmlFor="fromEmail">From Email</label>
              <TextInput
                type="email"
                id="fromEmail"
                name="fromEmail"
                value={settings.fromEmail}
                onChange={(e) => handleChange('fromEmail', e.target.value)}
                placeholder="noreply@example.com"
                disabled={saving || !settings.enabled}
              />
            </div>
          </div>
        </div>

        {/* Test Configuration */}
        {settings.enabled && (
          <div className="settings-group compact-group">
            <h3><Send size={18} /> Test Configuration</h3>
            <p className="section-hint">Verify your SMTP settings are working correctly</p>

            <div className="test-config-row">
              <LoadingButton
                loading={testing}
                className="btn-secondary"
                onClick={handleTestConnection}
                disabled={!settings.host || !settings.authUser}
                style={{ minWidth: '155px', height: '55px' }}
              >
                <Server size={16} /> Test Connection
              </LoadingButton>
              <div className="test-email-inline">
                <TextInput
                  type="email"
                  name="testEmail"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="recipient@example.com"
                  disabled={sendingTest}
                />
                <LoadingButton
                  loading={sendingTest}
                  className="btn-primary"
                  onClick={handleSendTestEmail}
                  disabled={!testEmail}
                  style={{ minWidth: '120px', height: '55px' }}
                >
                  <Send size={16} /> Send Test
                </LoadingButton>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? <><Loader2 size={16} className="spinner" /> Saving...</> : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

// About Settings - Version and system information
function AboutSettings() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInfo();
  }, []);

  const loadInfo = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getInfo();
      setInfo(response.data);
    } catch (err) {
      setError('Failed to load application info');
      console.error('Load info error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.length > 0 ? parts.join(' ') : '< 1m';
  };

  if (loading) {
    return (
      <div className="admin-section">
        <h2>About</h2>
        <div className="loading-spinner"><Loader2 className="spinner" size={24} /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-section">
        <h2>About</h2>
        <div className="message message-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-section">
      <h2>About Remidy</h2>
      <p className="section-description">Application version and system information</p>

      {/* Application Info */}
      <div className="info-card">
        <div className="info-card-header">
          <Info size={20} />
          <h3>Application</h3>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Name</span>
            <span className="info-value">{info.app.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Version</span>
            <span className="info-value version-badge">{info.app.version}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Description</span>
            <span className="info-value">{info.app.description || '—'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">License</span>
            <span className="info-value">{info.app.license}</span>
          </div>
          {info.app.buildDate && (
            <div className="info-item">
              <span className="info-label">Build Date</span>
              <span className="info-value">{new Date(info.app.buildDate).toLocaleString()}</span>
            </div>
          )}
          {info.app.buildCommit && (
            <div className="info-item">
              <span className="info-label">Build Commit</span>
              <span className="info-value mono">{info.app.buildCommit.substring(0, 8)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Runtime Info */}
      <div className="info-card">
        <div className="info-card-header">
          <Server size={20} />
          <h3>Runtime</h3>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Node.js</span>
            <span className="info-value">{info.runtime.nodeVersion}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Environment</span>
            <span className="info-value">{info.runtime.env}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Platform</span>
            <span className="info-value">{info.runtime.platform} ({info.runtime.arch})</span>
          </div>
          <div className="info-item">
            <span className="info-label">Uptime</span>
            <span className="info-value">{formatUptime(info.runtime.uptime)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Memory (Heap Used)</span>
            <span className="info-value">{formatBytes(info.runtime.memoryUsage.heapUsed)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Memory (Heap Total)</span>
            <span className="info-value">{formatBytes(info.runtime.memoryUsage.heapTotal)}</span>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="info-card">
        <div className="info-card-header">
          <Globe size={20} />
          <h3>System</h3>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Hostname</span>
            <span className="info-value">{info.system.hostname}</span>
          </div>
          <div className="info-item">
            <span className="info-label">CPUs</span>
            <span className="info-value">{info.system.cpus}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Total Memory</span>
            <span className="info-value">{formatBytes(info.system.totalMemory)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Free Memory</span>
            <span className="info-value">{formatBytes(info.system.freeMemory)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Load Average (1m)</span>
            <span className="info-value">{info.system.loadAvg[0].toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Database Info */}
      <div className="info-card">
        <div className="info-card-header">
          <Database size={20} />
          <h3>Database</h3>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">PostgreSQL</span>
            <span className="info-value">{info.database.version}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Size</span>
            <span className="info-value">{info.database.size}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Tables</span>
            <span className="info-value">{info.database.tables}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="info-card">
        <div className="info-card-header">
          <Users size={20} />
          <h3>Statistics</h3>
        </div>
        <div className="info-grid stats-grid">
          <div className="stat-box">
            <span className="stat-value">{info.stats.users}</span>
            <span className="stat-label">Users</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{info.stats.households}</span>
            <span className="stat-label">Households</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{info.stats.bills}</span>
            <span className="stat-label">Bills</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
